import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Globe, Code, TrendingUp, Bot } from "lucide-react";

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Portfolio", path: "/portfolio" },
    { name: "About", path: "/about" },
    { name: "Pricing", path: "/pricing" },
    { name: "Contact", path: "/contact" }
  ];

  const services = [
    { 
      name: "Web Development", 
      path: "/services/web-development", 
      icon: Globe
    },
    { 
      name: "Custom Software", 
      path: "/services/custom-software", 
      icon: Code
    },
    { 
      name: "AI Automation", 
      path: "/services/ai-automation", 
      icon: Bot
    },
    { 
      name: "Growth Marketing", 
      path: "/services/growth-marketing", 
      icon: TrendingUp
    }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav className="navbar" data-testid="navbar">
        {/* Logo */}
        <Link 
          to="/" 
          className="flex items-center gap-2 flex-shrink-0"
          data-testid="logo-link"
        >
          <img 
            src="/logo.svg" 
            alt="Averoan" 
            className="h-10 md:h-12 w-auto"
            style={{ minWidth: '40px' }}
          />
        </Link>
        
        {/* Desktop Navigation */}
        <div className="nav-desktop flex items-center gap-6">
          <Link 
            to="/" 
            className={`nav-link ${isActive('/') ? 'active' : ''}`}
            data-testid="nav-home"
          >
            Home
          </Link>
          
          {/* Services Dropdown - Fixed: no gap */}
          <div 
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button 
              className="nav-link flex items-center gap-1"
              data-testid="nav-services-dropdown"
            >
              Services 
              <ChevronDown 
                size={14} 
                className={`transition-transform duration-300 ${servicesOpen ? 'rotate-180' : ''}`}
              />
            </button>
            
            {/* Dropdown - sleek vertical design */}
            <div 
              className={`absolute left-0 top-full transition-all duration-300 ${servicesOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'}`}
            >
              {/* Invisible bridge */}
              <div className="h-3 w-full"></div>
              <div
                className="min-w-[280px] p-2 rounded-2xl overflow-hidden"
                style={{ 
                  background: 'linear-gradient(145deg, rgba(24, 24, 27, 0.98), rgba(15, 15, 18, 0.98))',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(212, 255, 0, 0.1)',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255,255,255,0.05)'
                }}
              >
                {services.map((service, index) => (
                  <Link
                    key={service.name}
                    to={service.path}
                    className="group flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-300 hover:bg-[#D4FF00]"
                    data-testid={`nav-service-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div 
                      className="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-110 bg-[rgba(212,255,0,0.1)] group-hover:bg-white"
                    >
                      <service.icon 
                        size={20} 
                        className="text-[#D4FF00] transition-colors duration-300 group-hover:text-black" 
                      />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-white transition-colors duration-300 group-hover:text-black">
                        {service.name}
                      </span>
                      <span className="text-xs text-zinc-500 transition-colors duration-300 group-hover:text-black/60">
                        {index === 0 && "Websites & E-commerce"}
                        {index === 1 && "CRM & Automation"}
                        {index === 2 && "Chatbots & AI Tools"}
                        {index === 3 && "SEO & Advertising"}
                      </span>
                    </div>
                  </Link>
                ))}
                
                {/* Bottom CTA */}
                <div className="mt-2 pt-2 border-t border-white/5">
                  <Link
                    to="/consultation"
                    className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider text-[#D4FF00] hover:bg-[#D4FF00]/10 transition-all duration-300"
                  >
                    View All Services
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>
          
          {navLinks.slice(1).map((link) => (
            <Link 
              key={link.name} 
              to={link.path} 
              className={`nav-link ${isActive(link.path) ? 'active' : ''}`}
              data-testid={`nav-${link.name.toLowerCase()}`}
            >
              {link.name}
            </Link>
          ))}
          
          <Link 
            to="/consultation" 
            className="bg-[#D4FF00] text-black font-semibold text-xs uppercase tracking-wide py-2 px-5 rounded-full hover:scale-105 transition-transform whitespace-nowrap"
            data-testid="nav-get-started"
          >
            Get Started
          </Link>
        </div>

        <button 
          className="nav-mobile-btn text-white"
          onClick={() => setMobileMenuOpen(true)}
          data-testid="mobile-menu-toggle"
        >
          <Menu size={24} />
        </button>
      </nav>

      {mobileMenuOpen && (
        <div className="mobile-menu" data-testid="mobile-menu">
          <button 
            className="absolute top-6 right-6 text-white"
            onClick={() => setMobileMenuOpen(false)}
            data-testid="mobile-menu-close"
          >
            <X size={28} />
          </button>
          
          <Link to="/" onClick={() => setMobileMenuOpen(false)}>Home</Link>
          
          {/* Mobile Services Dropdown - matches other menu items */}
          <div className="flex flex-col items-center">
            <button 
              onClick={() => setServicesOpen(!servicesOpen)}
              className="flex items-center gap-3 uppercase"
              style={{ 
                fontFamily: '"Clash Display", sans-serif',
                fontSize: '32px',
                fontWeight: 600,
                color: 'var(--foreground)',
                textDecoration: 'none'
              }}
            >
              Services
              <ChevronDown 
                size={24} 
                className={`transition-transform duration-300 ${servicesOpen ? 'rotate-180' : ''}`}
              />
            </button>
            
            <div className={`overflow-hidden transition-all duration-300 ${servicesOpen ? 'max-h-64 opacity-100 mt-6' : 'max-h-0 opacity-0'}`}>
              <div className="flex flex-col items-center gap-5">
                {services.map((service) => (
                  <Link
                    key={service.name}
                    to={service.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-2 text-zinc-400 hover:text-[#D4FF00] transition-colors"
                    style={{ fontSize: '20px', fontFamily: '"Clash Display", sans-serif', fontWeight: 600, textTransform: 'uppercase' }}
                    data-testid={`mobile-nav-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <service.icon size={18} className="text-[#D4FF00]" />
                    {service.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          
          <Link to="/portfolio" onClick={() => setMobileMenuOpen(false)}>Portfolio</Link>
          <Link to="/about" onClick={() => setMobileMenuOpen(false)}>About</Link>
          <Link to="/pricing" onClick={() => setMobileMenuOpen(false)}>Pricing</Link>
          <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
          <Link 
            to="/consultation" 
            onClick={() => setMobileMenuOpen(false)} 
            className="font-semibold text-sm uppercase tracking-wide py-3 px-8 rounded-full hover:scale-105 transition-transform mt-4"
            style={{ fontFamily: '"Clash Display", sans-serif', backgroundColor: '#D4FF00', color: '#000000' }}
          >
            Get Started
          </Link>
        </div>
      )}
    </>
  );
};

export default Navbar;
